import * as React from "react";
import { Skeleton } from "@repo/ui";
import { Inbox } from "lucide-react";
import { formatToApiDate } from "@repo/utils";
import { ReminderCard } from "./reminder-card";
import type { Reminder } from "@/hooks/use-reminders";

interface RemindersListProps {
  reminders: Reminder[];
  isLoading: boolean;
  onToggleComplete: (id: string) => void;
  onEdit: (reminder: Reminder) => void;
  onDelete: (id: string) => void;
  onViewDetail: (reminder: Reminder) => void;
  currentUserId?: string;
}

interface GroupedReminders {
  overdue: Reminder[];
  today: Reminder[];
  upcoming: Reminder[];
  completed: Reminder[];
}

function groupReminders(reminders: Reminder[]): GroupedReminders {
  const today = formatToApiDate(new Date());

  return reminders.reduce<GroupedReminders>(
    (acc, reminder) => {
      if (reminder.is_completed) {
        acc.completed.push(reminder);
      } else if (reminder.due_date < today) {
        acc.overdue.push(reminder);
      } else if (reminder.due_date === today) {
        acc.today.push(reminder);
      } else {
        acc.upcoming.push(reminder);
      }
      return acc;
    },
    { overdue: [], today: [], upcoming: [], completed: [] }
  );
}

export function RemindersList({
  reminders,
  isLoading,
  onToggleComplete,
  onEdit,
  onDelete,
  onViewDetail,
  currentUserId,
}: RemindersListProps) {
  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-28 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  if (reminders.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="rounded-full bg-muted p-4 mb-4">
          <Inbox className="h-8 w-8 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-medium">No reminders</h3>
        <p className="text-sm text-muted-foreground mt-1 max-w-sm">
          Create a reminder to keep track of tasks, follow-ups, and important
          pharmacy activities.
        </p>
      </div>
    );
  }

  const groups = groupReminders(reminders);

  return (
    <div data-testid="reminders-list" className="space-y-6">
      {/* Overdue */}
      {groups.overdue.length > 0 && (
        <ReminderGroup
          title="Overdue"
          count={groups.overdue.length}
          variant="destructive"
          reminders={groups.overdue}
          onToggleComplete={onToggleComplete}
          onEdit={onEdit}
          onDelete={onDelete}
          onViewDetail={onViewDetail}
          currentUserId={currentUserId}
        />
      )}

      {/* Today */}
      {groups.today.length > 0 && (
        <ReminderGroup
          title="Due Today"
          count={groups.today.length}
          variant="warning"
          reminders={groups.today}
          onToggleComplete={onToggleComplete}
          onEdit={onEdit}
          onDelete={onDelete}
          onViewDetail={onViewDetail}
          currentUserId={currentUserId}
        />
      )}

      {/* Upcoming */}
      {groups.upcoming.length > 0 && (
        <ReminderGroup
          title="Upcoming"
          count={groups.upcoming.length}
          variant="default"
          reminders={groups.upcoming}
          onToggleComplete={onToggleComplete}
          onEdit={onEdit}
          onDelete={onDelete}
          onViewDetail={onViewDetail}
          currentUserId={currentUserId}
        />
      )}

      {/* Completed */}
      {groups.completed.length > 0 && (
        <ReminderGroup
          title="Completed"
          count={groups.completed.length}
          variant="muted"
          reminders={groups.completed}
          onToggleComplete={onToggleComplete}
          onEdit={onEdit}
          onDelete={onDelete}
          onViewDetail={onViewDetail}
          currentUserId={currentUserId}
          defaultCollapsed
        />
      )}
    </div>
  );
}

interface ReminderGroupProps {
  title: string;
  count: number;
  variant: "destructive" | "warning" | "default" | "muted";
  reminders: Reminder[];
  onToggleComplete: (id: string) => void;
  onEdit: (reminder: Reminder) => void;
  onDelete: (id: string) => void;
  onViewDetail: (reminder: Reminder) => void;
  currentUserId?: string;
  defaultCollapsed?: boolean;
}

function ReminderGroup({
  title,
  count,
  variant,
  reminders,
  onToggleComplete,
  onEdit,
  onDelete,
  onViewDetail,
  currentUserId,
  defaultCollapsed = false,
}: ReminderGroupProps) {
  const [collapsed, setCollapsed] = React.useState(defaultCollapsed);

  const dotColor = {
    destructive: "bg-red-500",
    warning: "bg-amber-500",
    default: "bg-muted-foreground",
    muted: "bg-muted-foreground/50",
  }[variant];

  return (
    <div>
      <button
        data-testid={`reminder-group-toggle-${title.toLowerCase().replace(/\s+/g, "-")}`}
        className="flex items-center gap-2 mb-3 hover:opacity-80 transition-opacity"
        onClick={() => setCollapsed(!collapsed)}
      >
        <span className={`h-2 w-2 rounded-full ${dotColor}`} />
        <h3 className="text-xs font-semibold text-muted-foreground">{title}</h3>
        <span className="text-xs text-muted-foreground">({count})</span>
        <span className="text-xs text-muted-foreground">
          {collapsed ? "+" : "−"}
        </span>
      </button>

      {!collapsed && (
        <div
          data-testid={`reminder-group-${title.toLowerCase().replace(/\s+/g, "-")}`}
          className="space-y-2"
        >
          {reminders.map((reminder) => (
            <ReminderCard
              key={reminder.id}
              reminder={reminder}
              onToggleComplete={onToggleComplete}
              onEdit={onEdit}
              onDelete={onDelete}
              onViewDetail={onViewDetail}
              currentUserId={currentUserId}
            />
          ))}
        </div>
      )}
    </div>
  );
}
